import PropTypes from 'prop-types';
import { createContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  getAuthToken,
  setAuthToken,
  getAuthUser,
  setAuthUser,
  clearAuthSession
} from 'lib/authStorage';

// ==============================|| AUTH CONTEXT ||============================== //

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null
};

export const AuthContext = createContext(undefined);

// ==============================|| AUTH PROVIDER ||============================== //

export function AuthProvider({ children }) {
  const [state, setState] = useState(initialState);

  // Verify token with backend on mount
  const verifyToken = useCallback(async () => {
    const token = getAuthToken();

    if (!token) {
      setState({
        isAuthenticated: false,
        isInitialized: true,
        user: null
      });
      return;
    }

    try {
      const response = await axios.get('/api/auth/me');

      if (response.data.success && response.data.user) {
        setAuthUser(response.data.user);
        setState({
          isAuthenticated: true,
          isInitialized: true,
          user: response.data.user
        });
      } else {
        clearAuthSession();
        setState({
          isAuthenticated: false,
          isInitialized: true,
          user: null
        });
      }
    } catch {
      clearAuthSession();
      setState({
        isAuthenticated: false,
        isInitialized: true,
        user: null
      });
    }
  }, []);

  // Login function
  const login = useCallback(async (email, password) => {
    const response = await axios.post('/api/auth/login', { email, password });

    if (response.data.success) {
      const { token, user } = response.data;
      setAuthToken(token);
      setAuthUser(user);
      setState({
        isAuthenticated: true,
        isInitialized: true,
        user
      });
      return { success: true, user };
    }

    const errMsg = response.data?.error?.message || response.data?.message || 'Login failed';
    throw new Error(String(errMsg));
  }, []);

  // Logout function
  const logout = useCallback(() => {
    clearAuthSession();
    setState({
      isAuthenticated: false,
      isInitialized: true,
      user: null
    });
  }, []);

  // Update user data
  const updateUser = useCallback((userData) => {
    setAuthUser(userData);
    setState((prev) => ({
      ...prev,
      user: userData
    }));
  }, []);

  // Initial token verification on mount
  useEffect(() => {
    verifyToken();
  }, [verifyToken]);

  // Listen for auth:unauthorized events from axios interceptor
  useEffect(() => {
    const handleUnauthorized = () => {
      setState({
        isAuthenticated: false,
        isInitialized: true,
        user: null
      });
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);

    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
    };
  }, []);

  const memoizedValue = useMemo(
    () => ({
      ...state,
      login,
      logout,
      updateUser,
      verifyToken
    }),
    [state, login, logout, updateUser, verifyToken]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = { children: PropTypes.node };
